import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Bus, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@attg.eus");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-secondary" data-testid="login-page">
      {/* Brand / image side */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1553455010-bdb488ac12e5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
          alt="Gipuzkoa"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-secondary/85" />
        <div className="relative z-10 flex items-center gap-3 text-white">
          <div className="h-11 w-11 rounded-xl bg-primary flex items-center justify-center">
            <Bus size={24} />
          </div>
          <div>
            <div className="font-bold text-lg leading-tight" style={{ fontFamily: "Work Sans" }}>ATTG Data Master</div>
            <div className="text-xs text-white/70">Autoridad del Transporte de Gipuzkoa</div>
          </div>
        </div>
        <div className="relative z-10 text-white max-w-md">
          <h1 className="text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "Work Sans" }}>
            Gestión de Servicios Urbanos
          </h1>
          <p className="text-white/70 text-base leading-relaxed">
            Carga, edita y exporta los datos de cada operador: líneas, itinerarios,
            paradas, cocheras, empleados, vehículos y turnos. Importación directa desde GTFS.
          </p>
        </div>
        <div className="relative z-10 text-white/40 text-xs">© {new Date().getFullYear()} ATTG · Gipuzkoa</div>
      </div>

      {/* Form side */}
      <div className="flex items-center justify-center p-6 bg-background">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-primary flex items-center justify-center text-white">
              <Bus size={22} />
            </div>
            <span className="font-bold text-lg" style={{ fontFamily: "Work Sans" }}>ATTG Data Master</span>
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 mb-1" style={{ fontFamily: "Work Sans" }}>
            Iniciar sesión
          </h2>
          <p className="text-sm text-muted-foreground mb-7">Accede al panel de gestión.</p>

          {err && (
            <div className="mb-4 rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700" data-testid="login-error">
              {err}
            </div>
          )}

          <label className="text-sm font-medium text-slate-700">Correo electrónico</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            className="mt-1 mb-4 flex h-10 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            required
          />

          <label className="text-sm font-medium text-slate-700">Contraseña</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="login-password"
            placeholder="••••••••"
            className="mt-1 mb-6 flex h-10 w-full rounded-md border border-input bg-card px-3 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            required
          />

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground h-10 px-4 text-sm font-medium shadow-sm hover:bg-primary/90 active:scale-[0.99] transition-all disabled:opacity-60"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Entrar
          </button>
        </form>
      </div>
    </div>
  );
}
